import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeUp, fadeUpStagger, viewport } from '../utils/animations';

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home',           item: 'https://bayareapodcast.studio/' },
    { '@type': 'ListItem', position: 2, name: 'About',          item: 'https://bayareapodcast.studio/about' },
  ],
};

export default function About() {
  // ── meta tags ────────────────────────────────────────────────────────────
  useEffect(() => {
    document.title = 'About & Studio Location | Bay Area Podcast Studio';

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.replace('meta[', '').replace(']', '').split('="');
        el.setAttribute(attrName, attrVal.replace('"', ''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
      return el;
    };

    const prevDesc    = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
    const prevOgTitle = document.querySelector('meta[property="og:title"]')?.getAttribute('content') || '';
    const prevOgDesc  = document.querySelector('meta[property="og:description"]')?.getAttribute('content') || '';
    const prevOgUrl   = document.querySelector('meta[property="og:url"]')?.getAttribute('content') || '';
    const prevTwTitle = document.querySelector('meta[name="twitter:title"]')?.getAttribute('content') || '';
    const prevTwDesc  = document.querySelector('meta[name="twitter:description"]')?.getAttribute('content') || '';

    setMeta('meta[name="description"]', 'content',
      "Learn about Bay Area Podcast Studio in Fremont, CA. Built on the 'Touch Nothing' standard for enterprise executives and creators. Corporate Ready certified and graduate of the Golden State Warriors Franchise Fund."
    );
    setMeta('meta[property="og:title"]',       'content', 'About Bay Area Podcast Studio | Fremont, CA');
    setMeta('meta[property="og:description"]', 'content', 'About Bay Area Podcast Studio. Operational philosophy, certified corporate trust, and studio location in Fremont, CA.');
    setMeta('meta[property="og:url"]',         'content', 'https://bayareapodcast.studio/about');
    setMeta('meta[name="twitter:title"]',      'content', 'About Bay Area Podcast Studio | Fremont, CA');
    setMeta('meta[name="twitter:description"]','content', 'About Bay Area Podcast Studio. Operational philosophy, certified corporate trust, and studio location in Fremont, CA.');

    return () => {
      document.title = 'Bay Area Podcast Studio | Premium Video Podcast & Content Studio';
      setMeta('meta[name="description"]',       'content', prevDesc);
      setMeta('meta[property="og:title"]',      'content', prevOgTitle);
      setMeta('meta[property="og:description"]','content', prevOgDesc);
      setMeta('meta[property="og:url"]',        'content', prevOgUrl);
      setMeta('meta[name="twitter:title"]',     'content', prevTwTitle);
      setMeta('meta[name="twitter:description"]','content', prevTwDesc);
    };
  }, []);

  // ── BreadcrumbList JSON-LD ───────────────────────────────────────────────
  useEffect(() => {
    const id = 'ld-about-breadcrumb';
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(BREADCRUMB_JSON_LD);
    return () => {
      const tag = document.getElementById(id);
      if (tag) tag.remove();
    };
  }, []);

  return (
    <main className="bg-[#FFFFFF] text-[#000000] min-h-screen pt-[72px] overflow-x-hidden">

      {/* SECTION 1: HERO THESIS */}
      <motion.section
        className="max-w-5xl mx-auto px-5 md:px-12 pt-12 md:pt-24 pb-10 md:pb-12 text-left"
        initial="hidden"
        animate="visible"
        variants={fadeUpStagger}
      >
        <motion.h1
          className="text-3xl sm:text-5xl md:text-6xl font-black uppercase text-black mb-4 tracking-tight leading-tight"
          variants={fadeUp}
        >
          THE TOUCH NOTHING STANDARD
        </motion.h1>
        <motion.p
          className="text-base md:text-xl text-neutral-800 font-medium max-w-3xl leading-relaxed"
          variants={fadeUp}
        >
          We built Bay Area Podcast Studio to eliminate technical friction for executives, founders, and creative teams. You walk in, deliver your message, and walk out with high-quality media. We handle everything in between.
        </motion.p>
      </motion.section>

      {/* SECTION 2: OPERATOR & CERTIFIED TRUST */}
      <section className="max-w-7xl mx-auto px-5 md:px-12 py-12 md:py-16 border-t border-neutral-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start md:items-center">

          <motion.div
            className="w-full bg-neutral-900 border border-neutral-200 shadow-xl overflow-hidden relative group"
            style={{ aspectRatio: '4/5' }}
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={viewport}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/11ef80cf7995af8acd5e66a4e59e4dfd.png"
              alt="Aaron founder and cinematographer at Bay Area Podcast Studio holding camera"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div
              className="absolute bottom-0 left-0 right-0 px-5 md:px-6 py-4 md:py-5"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.20) 70%, transparent 100%)' }}
            >
              <p style={{ fontFamily: "'Inter', 'SF Pro Display', sans-serif", fontSize: '1.25rem', fontWeight: 600, color: '#ffffff', lineHeight: 1.25, letterSpacing: '0.02em', marginBottom: '4px' }}>
                Aaron Figueroa
              </p>
              <p style={{ fontFamily: "'Inter', 'SF Pro Display', sans-serif", fontSize: '0.62rem', fontWeight: 600, color: 'rgba(255,255,255,0.60)', letterSpacing: '0.20em', textTransform: 'uppercase' }}>
                Founder &amp; Videographer
              </p>
            </div>
          </motion.div>

          <motion.div
            className="w-full min-w-0"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={fadeUpStagger}
          >
            <motion.p className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2" variants={fadeUp}>
              OPERATOR LED
            </motion.p>
            <motion.h2
              className="text-2xl md:text-3xl font-black uppercase text-black mb-5 md:mb-6 tracking-tight leading-tight"
              variants={fadeUp}
            >
              BUILT FOR THE BAY.<br />BACKED BY THE BEST.
            </motion.h2>
            <motion.p className="text-base text-neutral-800 font-medium mb-4 leading-relaxed" variants={fadeUp}>
              Every session is personally directed and engineered to ensure zero technical downtime, pristine audio and video capture, and total client privacy.
            </motion.p>
            <motion.p className="text-base text-neutral-800 font-medium mb-7 md:mb-8 leading-relaxed" variants={fadeUp}>
              We are a certified Corporate Ready graduate of the Franchise Fund (presented by the Golden State Warriors, United Airlines, and WRMSDC), meeting the strict Tier-1 vendor, NDA, and operational requirements demanded by enterprise companies.
            </motion.p>
            <motion.div
              className="flex flex-wrap items-center gap-4 sm:gap-6 md:gap-8 pt-5 md:pt-6 border-t border-neutral-200"
              variants={fadeUp}
            >
              <div className="flex items-center justify-center flex-shrink-0 rounded-sm" style={{ width: '80px', height: '80px', padding: '6px', backgroundColor: '#f9f9f9' }}>
                <img
                  src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/f894fb9f7838d4ce47fe98dac08d1e4d.png"
                  alt="Corporate Ready Certification badge WRMSDC"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
              <div className="hidden sm:block h-14 w-px bg-neutral-200 flex-shrink-0" />
              <div className="flex items-center justify-center flex-1 min-w-0 rounded-sm" style={{ height: '72px', minWidth: '120px', padding: '6px', backgroundColor: '#f9f9f9' }}>
                <img
                  src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/428c5a8746046b17b90710574a1e97f0.png"
                  alt="Golden State Warriors Franchise Fund presented by United Airlines logo"
                  style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'left center' }}
                />
              </div>
            </motion.div>
          </motion.div>

        </div>
      </section>

      {/* SECTION 3: LOCATION & MAP */}
      <motion.section
        className="max-w-7xl mx-auto px-5 md:px-12 py-12 md:py-16 border-t border-neutral-200"
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        variants={fadeUp}
      >
        <div className="mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-black uppercase text-black mb-2 tracking-tight">STUDIO LOCATION</h2>
          <p className="text-sm md:text-base text-neutral-800 font-semibold leading-relaxed">
            Based in Fremont, CA. Convenient access for Silicon Valley, San Francisco, and East Bay executive teams.
          </p>
        </div>
        <div className="w-full bg-neutral-100 border border-neutral-300 shadow-md overflow-hidden relative" style={{ height: 'clamp(260px, 50vw, 420px)' }}>
          <iframe
            title="Bay Area Podcast Studio Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3156.2370432!2d-121.9886!3d37.5485!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808fc0e5c2e4a1d3%3A0x1a2b3c4d5e6f7a8b!2s3909+Washington+Blvd+%23206%2C+Fremont%2C+CA+94538!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0, filter: 'grayscale(20%) contrast(1.05) saturate(0.85)', display: 'block' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </motion.section>

      {/* SECTION 4: CLOSING CTA */}
      <motion.section
        className="max-w-7xl mx-auto px-5 md:px-12 py-16 md:py-20 border-t border-neutral-200 text-center"
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        variants={fadeUpStagger}
      >
        <motion.h2
          className="text-3xl sm:text-4xl md:text-5xl font-black uppercase text-black mb-3 tracking-tight leading-tight"
          variants={fadeUp}
        >
          EXECUTE YOUR VISION.
        </motion.h2>
        <motion.p
          className="text-base md:text-lg text-neutral-800 font-semibold mb-8 max-w-md mx-auto leading-relaxed"
          variants={fadeUp}
        >
          Book the studio or contact our production team for custom video services.
        </motion.p>
        <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" variants={fadeUp}>
          <Link
            to="/studio-sessions"
            className="bg-black text-white hover:bg-neutral-800 text-xs font-bold uppercase tracking-wider px-8 py-4 border border-black transition-all w-full sm:w-auto text-center"
          >
            BOOK THE STUDIO
          </Link>
          <Link
            to="/video-services#contact"
            className="bg-transparent text-black border border-black hover:bg-neutral-100 text-xs font-bold uppercase tracking-wider px-8 py-4 transition-all w-full sm:w-auto text-center"
          >
            START A PROJECT
          </Link>
        </motion.div>
      </motion.section>

    </main>
  );
}